import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Container, Row, Col, Card, Button, Badge } from 'react-bootstrap';
import { FiPlus, FiUsers, FiSettings, FiArrowRight } from 'react-icons/fi';
import { HiOutlineOfficeBuilding } from 'react-icons/hi';
import CreateOrg from './CreateOrg';
import { useOrgContext } from '../../context/org';
import { IOrg } from '../../models/org';

function SelectOrg() {
	const { orgs, setOrg } = useOrgContext();
	const [showCreateModal, setShowCreateModal] = useState(false);
	const navigate = useNavigate();

	const handleSelectOrg = (selectedOrg: IOrg) => {
		setOrg(selectedOrg);
		navigate(`/dashboard/orgs/${selectedOrg.id}`);
	};

	return (
		<div className="organization-selector">
			<Container fluid style={{ maxWidth: '1200px', margin: '0 auto', padding: '2rem' }}>
				{/* Header Section */}
				<div className="header-section">
					<div className="d-flex justify-content-between align-items-center mb-4">
						<div>
							<h1 className="display-6 fw-bold text-light mb-2">
								<HiOutlineOfficeBuilding className="me-3" />
								Organizations
							</h1>
							<p className="mb-0" style={{color: '#ccc'}}>
								{orgs && orgs.length > 0 
									? 'Select an organization to manage your agents and data sources'
									: 'Create your first organization to get started'
								}
							</p>
						</div>
						<Button
							variant="primary"
							size="lg"
							className="d-flex align-items-center gap-2"
							onClick={() => setShowCreateModal(true)}
						>
							<FiPlus size={18} />
							New Organization
						</Button>
					</div>
				</div>

				{/* Organizations Grid */}
				<Row className="g-4">
					{!orgs || orgs.length === 0 ? (
						<Col xs={12}>
							<div className="empty-state text-center py-5">
								<div className="empty-icon mb-4">
									<HiOutlineOfficeBuilding size={64} className="text-light" />
								</div>
								<h3 className="text-light mb-3">No Organizations Yet</h3>
								<p className="mb-4" style={{color: '#ccc'}}>
									Create your first organization to start building and managing AI agents.
								</p>
								<Button
									variant="primary"
									size="lg"
									className="d-flex align-items-center gap-2 mx-auto"
									onClick={() => setShowCreateModal(true)}
								>
									<FiPlus size={18} />
									Create Organization
								</Button>
							</div>
						</Col>
					) : (
						orgs.map((orgItem) => (
							<Col key={orgItem.id} xs={12} md={6} lg={4}>
								<Card 
									className="org-card h-100"
									onClick={() => handleSelectOrg(orgItem)}
									style={{ cursor: 'pointer' }}
								>
									<Card.Body className="d-flex flex-column">
										<div className="d-flex justify-content-between align-items-start mb-3">
											<div className="org-icon">
												<HiOutlineOfficeBuilding size={24} className="text-primary" />
											</div>
											<Badge bg="success" className="status-badge">
												Active
											</Badge>
										</div>
										
										<div className="flex-grow-1">
											<Card.Title className="text-white mb-2 h5">
												{orgItem.name}
											</Card.Title>
											{orgItem.description && (
												<Card.Text className="text-light mb-3" style={{ opacity: 0.8 }}>
													{orgItem.description}
												</Card.Text>
											)}
										</div>

										<div className="org-stats mb-3">
											<div className="d-flex align-items-center gap-3 text-light small" style={{ opacity: 0.7 }}>
												<div className="d-flex align-items-center gap-1">
													<FiUsers size={14} />
													<span>Members</span>
												</div>
												<div className="d-flex align-items-center gap-1">
													<FiSettings size={14} />
													<span>Agents</span>
												</div>
											</div>
										</div>

										<div className="d-flex justify-content-between align-items-center">
											<Button
												variant="outline-light"
												size="sm"
												className="flex-grow-1 me-2"
												onClick={(e) => {
													e.stopPropagation();
													handleSelectOrg(orgItem);
												}}
											>
												Select Organization
											</Button>
											<Button
												variant="link"
												className="p-2 text-light"
												onClick={(e) => {
													e.stopPropagation();
													handleSelectOrg(orgItem);
												}}
											>
												<FiArrowRight size={16} />
											</Button>
										</div>
									</Card.Body>
								</Card>
							</Col>
						))
					)}
				</Row>

				{/* Quick Actions */}
				{orgs && orgs.length > 0 && (
					<div className="quick-actions mt-5 pt-4 border-top border-secondary">
						<Row>
							<Col md={6}>
								<h5 className="text-light mb-3">Quick Actions</h5>
								<div className="d-flex gap-2 flex-wrap">
									<Button
										variant="outline-light"
										size="sm"
										onClick={() => setShowCreateModal(true)}
									>
										<FiPlus size={14} className="me-1" />
										New Organization
									</Button>
								</div>
							</Col>
							<Col md={6}>
								<h5 className="text-light mb-3">Need Help?</h5>
								<p className="small" style={{ color: '#ccc' }}>
									Organizations help you separate different projects or teams. 
									Each organization has its own agents, data sources, and members.
								</p>
							</Col>
						</Row>
					</div>
				)}
			</Container>

			{/* Create Organization Modal */}
			<CreateOrg 
				show={showCreateModal} 
				setShow={setShowCreateModal} 
			/>
		</div>
	);
}

export default SelectOrg;